import React from 'react';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import {
    Box,
    Button,
    ClickAwayListener,
    Collapse,
    createStyles,
    Grow,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    makeStyles,
    MenuList,
    Paper,
    Popper,
    Theme,
} from '@material-ui/core';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import { mainTheme } from '../../Theme';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import InfoIcon from '@material-ui/icons/Info';
import ImportContactsIcon from '@material-ui/icons/ImportContacts';
import UpdateIcon from '@material-ui/icons/Update';
import { ResourceEnum } from './UserCardV2';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
        },
        // Same pill-trigger hover treatment as UserCardV2.tsx's own triggerPill/triggerPillOpen -
        // explicit request for visual parity between the two header dropdowns.
        triggerPill: {
            display: 'flex',
            alignItems: 'center',
            padding: '4px 4px 4px 4px',
            borderRadius: '24px',
            border: '1px solid transparent',
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                backgroundColor: mainTheme.CommonColor.LightBlue2,
                borderColor: mainTheme.CommonColor.LightBlue1,
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                transform: 'translateY(-1px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        triggerPillOpen: {
            backgroundColor: mainTheme.CommonColor.LightBlue2,
            borderColor: mainTheme.CommonColor.LightBlue1,
            boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        },
        button: {
            textTransform: 'none',
            fontSize: '13px',
            color: mainTheme.TemplateColor.Primary,
            minWidth: 'auto',
            padding: '2px 4px',
            borderRadius: '999px',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        arrowIcon: {
            fontSize: '18px',
            color: mainTheme.TextColor.Tertiary,
            transition: 'transform 0.2s ease, color 0.15s ease',
        },
        arrowIconOpen: {
            color: mainTheme.TemplateColor.Primary,
            transform: 'rotate(180deg)',
        },
        menuPaper: {
            marginTop: theme.spacing(1),
            minWidth: '260px',
            maxWidth: '280px',
            borderRadius: '12px',
            border: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            boxShadow: '0 10px 40px rgba(35,37,59,0.16)',
            overflow: 'hidden',
        },
        sectionLabel: {
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Tertiary,
            lineHeight: '1.4',
            padding: theme.spacing(1.5, 2, 0.5),
        },
        iconChip: {
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            backgroundColor: mainTheme.CommonColor.LightBlue2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        listItemModern: {
            borderRadius: '8px',
            margin: '2px 8px',
            width: 'auto',
            transition: 'background-color 0.15s ease',
            '&:hover': {
                backgroundColor: mainTheme.CommonColor.LightBlue2,
            },
        },
        listItem: {
            color: mainTheme.TemplateColor.Primary,
        },
        listItemIcon: {
            minWidth: '40px',
        },
        icon: {
            color: mainTheme.TemplateColor.Primary,
            fontSize: '16px',
        },
        nested: {
            paddingLeft: theme.spacing(4),
        },
    }),
);

export const SelfHelp = () => {

    const classes = useStyles();

    const [open, setOpen] = React.useState(false);

    const [resourceOpen, setResourceOpen] = React.useState(false);

    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        setOpen((currentOpen) => !currentOpen);
    };

    const handleClose = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        }
    };

    const handleVideoGuide = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        window.open('https://youtu.be/EDcIcjz2-MM', '_blank');

        setOpen(false);
    };

    const handleTrainingWebinars = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        window.open('https://www.redraincorp.com/training-webinars', '_blank');

        setOpen(false);
    };

    const handleTrainingUpdates = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        window.open('https://www.redraincorp.com/updates-and-tips', '_blank');

        setOpen(false);
    };

    const handleResourceOpen = () => {
        setResourceOpen(!resourceOpen);
    };

    const handleResource = (resource: ResourceEnum) => (event: React.MouseEvent<EventTarget>) => {
        window.location.href = `/${resource}`;
    };

    return (
       <Box className={classes.root}>
            <Box className={clsx(classes.triggerPill, open && classes.triggerPillOpen)}>
                <Button
                    size="small"
                    startIcon={<MenuBookOutlinedIcon style={{ fontSize: 18 }} />}
                    ref={anchorRef}
                    onClick={handleToggle}
                    endIcon={<ArrowDropDownIcon className={clsx(classes.arrowIcon, open && classes.arrowIconOpen)} />}
                    className={classes.button}
                >
                    Self Help
                </Button>
            </Box>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                // role={undefined}
                transition={true}
                disablePortal={true}
            >
                {({ TransitionProps, placement }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                        <Paper className={classes.menuPaper}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList autoFocusItem={open} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                                    <ListSubheader disableSticky={true} className={classes.sectionLabel}>Resources</ListSubheader>
                                    <ListItem button={true} onClick={handleResourceOpen} className={clsx(classes.listItem, classes.listItemModern)}>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <Box className={classes.iconChip}>
                                                <InfoIcon className={classes.icon} />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText primary="Resources" />
                                        {resourceOpen ? <ExpandLess /> : <ExpandMore />}
                                    </ListItem>
                                    <Collapse in={resourceOpen} timeout="auto" unmountOnExit={true}>
                                        <List component="div" disablePadding={true}>
                                            <ListItem button={true} onClick={handleResource(ResourceEnum.Acuity)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <ImportContactsIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Acuity" />
                                            </ListItem>
                                            <ListItem button={true} onClick={handleResource(ResourceEnum.CRM)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <ImportContactsIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="CRM" />
                                            </ListItem>
                                            <ListItem button={true} onClick={handleResource(ResourceEnum.Tasks)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <ImportContactsIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Tasks" />
                                            </ListItem>
                                            <ListItem button={true} onClick={handleResource(ResourceEnum.Matters)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <ImportContactsIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Matters" />
                                            </ListItem>
                                            <ListItem button={true} onClick={handleResource(ResourceEnum.Timesheet)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <ImportContactsIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Timesheet" />
                                            </ListItem>
                                        </List>
                                    </Collapse>

                                    <ListSubheader disableSticky={true} className={classes.sectionLabel}>Support &amp; Learning</ListSubheader>
                                    <ListItem onClick={handleVideoGuide} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <Box className={classes.iconChip}>
                                                <VideoLibraryIcon className={classes.icon} />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText primary="A Video Guide" />
                                    </ListItem>
                                    <ListItem onClick={handleTrainingWebinars} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <Box className={classes.iconChip}>
                                                <VideoLibraryIcon className={classes.icon} />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText primary="Training Webinars" />
                                    </ListItem>
                                    <ListItem onClick={handleTrainingUpdates} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <Box className={classes.iconChip}>
                                                <UpdateIcon className={classes.icon} />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText primary="Feature Updates" />
                                    </ListItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
       </Box>
    );
};
